import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
  LayersControl,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import FacilityDetailModal from "./FacilityDetailModal";
import MapillaryImage from "./MapillaryImage";
import "leaflet/dist/leaflet.css";
import "../styles/Map.css";

const { BaseLayer } = LayersControl;

const MapViewController = ({ facilities, shouldResetView }) => {
  const map = useMap();

  useEffect(() => {
    if (facilities.length > 0) {
      const isMobile = window.innerWidth <= 768;
      const defaultZoom = isMobile ? 6 : 7;

      if (shouldResetView) {
        map.setView([44.3148, -85.6024], defaultZoom);
      } else if (facilities.length === 1) {
        map.setView(
          [facilities[0].latitude, facilities[0].longitude],
          isMobile ? 11 : 12
        );
      } else if (facilities.length > 1 && facilities.length < 20) {
        const bounds = facilities.map((f) => [f.latitude, f.longitude]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [facilities, shouldResetView, map]);

  return null;
};

// One facility marker. The Mapillary thumbnail is only mounted once the
// popup is actually opened, so we don't fire thousands of API requests for
// every marker on the map at load time.
const FacilityMarker = ({ facility, color, radius, onViewDetails }) => {
  const [popupOpen, setPopupOpen] = useState(false);

  return (
    <CircleMarker
      center={[facility.latitude, facility.longitude]}
      radius={radius}
      pathOptions={{
        color,
        fillColor: color,
        fillOpacity: 0.7,
        weight: 2,
      }}
      eventHandlers={{
        popupopen: () => setPopupOpen(true),
        popupclose: () => setPopupOpen(false),
      }}
    >
      <Popup maxWidth={400}>
        <div className="popup-content">
          <h3>{facility.facilityName}</h3>
          <div className="popup-section">
            <p>
              <strong>🏢 Parent Company:</strong>{" "}
              {facility.parentCompany || "N/A"}
            </p>
            <p>
              <strong>📍 Address:</strong> {facility.streetAddress},{" "}
              {facility.city}, {facility.state} {facility.zip}
            </p>
            <p>
              <strong>🏛️ County:</strong> {facility.county}
            </p>
            <p>
              <strong>🏭 Industry:</strong> {facility.industrySector}
            </p>
            <p>
              <strong>🧪 Chemicals Reported:</strong>{" "}
              {facility.reports?.length || 1}
            </p>
            <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>
              {facility.reports?.map((r, i) => (
                <li key={i} style={{ fontSize: "0.85em" }}>
                  {r.chemical} {r.carcinogen === "YES" ? "⚠️" : ""}
                </li>
              ))}
            </ul>
          </div>
          {popupOpen && (
            <MapillaryImage
              latitude={facility.latitude}
              longitude={facility.longitude}
              variant="thumbnail"
            />
          )}
          <button
            className="view-details-button"
            onClick={() => onViewDetails(facility)}
          >
            📊 View Full Details & Charts
          </button>
        </div>
      </Popup>
    </CircleMarker>
  );
};

const Map = ({ groupedFacilities, shouldResetView = false }) => {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const center = [44.3148, -85.6024];
  const isMobile = window.innerWidth <= 768;
  const initialZoom = isMobile ? 6 : 7;

  const getColor = (reports) => {
    return reports?.some((r) => r.carcinogen === "YES") ? "#e74c3c" : "#3498db";
  };

  const getRadius = (totalReleases) => {
    if (!totalReleases || totalReleases === 0) return 6;
    if (totalReleases < 1000) return 8;
    if (totalReleases < 10000) return 12;
    if (totalReleases < 100000) return 16;
    return 22;
  };

  const handleMarkerClick = (facility) => {
    setSelectedFacility(facility);
    setIsModalOpen(true);
  };

  return (
    <>
      <MapContainer
        center={center}
        zoom={initialZoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <LayersControl position="topright">
          <BaseLayer checked name="Street Map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </BaseLayer>
          <BaseLayer name="Satellite">
            <TileLayer
              attribution="Tiles &copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </BaseLayer>
        </LayersControl>

        <MapViewController
          facilities={groupedFacilities || []}
          shouldResetView={shouldResetView}
        />

        <MarkerClusterGroup>
          {(groupedFacilities || []).map((facility, idx) => (
            <FacilityMarker
              key={idx}
              facility={facility}
              color={getColor(facility.reports)}
              radius={getRadius(facility.totalReleases)}
              onViewDetails={handleMarkerClick}
            />
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      <FacilityDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        facility={selectedFacility}
      />
    </>
  );
};

export default Map;
